import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Trash2,
  Save,
  X,
  AlertCircle,
  Loader2,
} from "lucide-react";

const EditManualQuestionModal = ({
  isOpen,
  onClose,
  question,
  onSave,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    question: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    difficulty: "medium",
    category: "General",
    subCategory: "",
    tags: [],
    newTag: "",
  });
  
  const [validationErrors, setValidationErrors] = useState({});

  // Initialize form data when question prop changes
  useEffect(() => {
    if (question) {
      setFormData({
        question: question.question || "",
        options: question.options || ["", "", "", ""],
        correctAnswer: question.options?.[question.correctAnswerIndex] || "",
        difficulty: question.difficulty?.toLowerCase() || "medium",
        category: question.category || "General",
        subCategory: question.subCategory || "",
        tags: question.tags || [],
        newTag: "",
      });
    }
  }, [question]);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.question || !formData.question.trim()) {
      errors.question = "Question text is required";
    }
    
    const filledOptions = formData.options.filter(opt => opt && opt.trim());
    if (filledOptions.length < 2) {
      errors.options = "At least 2 options are required";
    }
    
    if (!formData.correctAnswer) {
      errors.correctAnswer = "Correct answer must be selected";
    } else if (!filledOptions.includes(formData.correctAnswer)) {
      errors.correctAnswer = "Correct answer must be one of the provided options";
    }
    
    if (!formData.difficulty) {
      errors.difficulty = "Difficulty level is required";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const updatedQuestion = {
      question: formData.question.trim(),
      options: formData.options.filter(opt => opt && opt.trim()),
      correctAnswer: formData.correctAnswer,
      difficulty: formData.difficulty,
      category: formData.category || "General",
      subCategory: formData.subCategory || "",
      tags: formData.tags || [],
    };

    onSave(updatedQuestion);
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({ ...prev, options: newOptions }));
    
    // Clear validation error
    if (validationErrors.options) {
      setValidationErrors(prev => ({ ...prev, options: undefined }));
    }
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, ""]
      }));
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        options: newOptions,
        // Reset correct answer if it was the removed option
        correctAnswer: prev.correctAnswer === prev.options[index] ? "" : prev.correctAnswer
      }));
    }
  };

  const addTag = () => {
    if (formData.newTag.trim() && !formData.tags.includes(formData.newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, prev.newTag.trim()],
        newTag: ""
      }));
    }
  };

  const removeTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const handleClose = () => {
    setFormData({
      question: "",
      options: ["", "", "", ""],
      correctAnswer: "",
      difficulty: "medium",
      category: "General",
      subCategory: "",
      tags: [],
      newTag: "",
    });
    setValidationErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5 text-blue-600" />
            Edit Question
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Question Text */}
          <div className="space-y-2">
            <Label htmlFor="question">
              Question Text <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="question"
              placeholder="Enter your question here..."
              value={formData.question}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, question: e.target.value }));
                if (validationErrors.question) {
                  setValidationErrors(prev => ({ ...prev, question: undefined }));
                }
              }}
              className={validationErrors.question ? "border-red-500" : ""}
              rows={3}
            />
            {validationErrors.question && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {validationErrors.question}
              </p>
            )}
          </div>

          {/* Options */}
          <div className="space-y-2">
            <Label>
              Answer Options <span className="text-red-500">*</span>
            </Label>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
              {formData.options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm font-medium w-8">
                    {String.fromCharCode(65 + index)}.
                  </span>
                  <Input
                    placeholder={`Option ${String.fromCharCode(65 + index)}`}
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    className="flex-1"
                  />
                  {formData.options.length > 2 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOption(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {formData.options.length < 6 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addOption}
                  className="mt-2"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Option
                </Button>
              )}
            </div>
            {validationErrors.options && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {validationErrors.options}
              </p>
            )}
          </div>

          {/* Correct Answer */}
          <div className="space-y-2">
            <Label>
              Correct Answer <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.correctAnswer}
              onValueChange={(value) => {
                setFormData(prev => ({ ...prev, correctAnswer: value }));
                if (validationErrors.correctAnswer) {
                  setValidationErrors(prev => ({ ...prev, correctAnswer: undefined }));
                }
              }}
            >
              <SelectTrigger className={validationErrors.correctAnswer ? "border-red-500" : ""}>
                <SelectValue placeholder="Select correct answer" />
              </SelectTrigger>
              <SelectContent>
                {formData.options
                  .filter(opt => opt.trim())
                  .map((option, index) => (
                    <SelectItem key={index} value={option}>
                      {String.fromCharCode(65 + index)}. {option}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {validationErrors.correctAnswer && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {validationErrors.correctAnswer}
              </p>
            )}
          </div>

          {/* Question Metadata */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Difficulty Level <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.difficulty}
                onValueChange={(value) => setFormData(prev => ({ ...prev, difficulty: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Input
                placeholder="e.g., JavaScript, React, etc."
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Sub Category (Optional)</Label>
            <Input
              placeholder="e.g., Functions, Components, etc."
              value={formData.subCategory}
              onChange={(e) => setFormData(prev => ({ ...prev, subCategory: e.target.value }))}
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags (Optional)</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => removeTag(index)}
                  />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add a tag..."
                value={formData.newTag}
                onChange={(e) => setFormData(prev => ({ ...prev, newTag: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && addTag()}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={addTag}
                disabled={!formData.newTag.trim()}
              >
                Add
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditManualQuestionModal;